'use client';

import { useParams, useNavigate } from 'react-router-dom';
import { Calculator, BookOpen, FileText, DollarSign, BarChart3, Lock, TrendingUp, Copy, FileCheck, Wallet, AlertCircle, Settings, ChevronLeft } from 'lucide-react';
import ChartOfAccounts from './ChartOfAccounts';
import Journals from './Journals';
import JournalEntries from './JournalEntries';
import GeneralLedger from './GeneralLedger';
import FinancialStatements from './FinancialStatements';
import PeriodClosing from './PeriodClosing';
import Reports from './Reports';
import { AccountingTemplates } from './AccountingTemplates';
import { ChartSearch } from './ChartSearch';
import { VatManagement } from './VatManagement';
import { CashFlowManagement } from './CashFlowManagement';

interface SectionConfig {
  title: string;
  description: string;
  icon: any;
  component: any;
}

const sections: Record<string, SectionConfig> = {
  coa: {
    title: 'Pla de Comptes',
    description: 'Estructura i configuració de comptes comptables',
    icon: Calculator,
    component: ChartOfAccounts,
  },
  journals: {
    title: 'Diaris Comptables',
    description: 'Gestió de diaris i assentaments comptables',
    icon: BookOpen,
    component: Journals,
  },
  entries: {
    title: 'Assentaments Comptables',
    description: 'Entrada i gestió d\'assentaments comptables',
    icon: FileText,
    component: JournalEntries,
  },
  templates: {
    title: 'Plantilles d\'Assentaments',
    description: 'Gestiona plantilles reutilitzables per automatitzar assentaments',
    icon: Copy,
    component: AccountingTemplates,
  },
  search: {
    title: 'Cerca Avançada',
    description: 'Cerca i consulta de comptes comptables',
    icon: FileCheck,
    component: ChartSearch,
  },
  ledger: {
    title: 'Major General',
    description: 'Visualització del major general de comptes',
    icon: DollarSign,
    component: GeneralLedger,
  },
  statements: {
    title: 'Estats Financers',
    description: 'Generació d\'informes i estats financers',
    icon: BarChart3,
    component: FinancialStatements,
  },
  vat: {
    title: 'Gestió de l\'IVA',
    description: 'Llibre d\'IVA, registres i informes fiscals',
    icon: FileText,
    component: VatManagement,
  },
  cashflow: {
    title: 'Tresoreria i Cash Flow',
    description: 'Projeccions de tresoreria i anàlisi del flux de caixa',
    icon: Wallet,
    component: CashFlowManagement,
  },
  reports: {
    title: 'Models Fiscals',
    description: 'Generació d\'informes tributaris i declaracions',
    icon: TrendingUp,
    component: Reports,
  },
  closing: {
    title: 'Tancament de Període',
    description: 'Processament del tancament comptable del període',
    icon: Lock,
    component: PeriodClosing,
  },
  audit: {
    title: 'Auditoria Comptable',
    description: 'Registre de canvis i traçabilitat d\'operacions',
    icon: AlertCircle,
    component: () => (
      <div className="p-6 bg-card rounded-lg border border-border">
        <h2 className="text-sm font-semibold mb-2">Auditoria</h2>
        <p className="text-muted-foreground">Registre de canvis i traçabilitat immutable</p>
      </div>
    ),
  },
  config: {
    title: 'Configuració Comptable',
    description: 'Paràmetres i configuració del sistema',
    icon: Settings,
    component: () => (
      <div className="p-6 bg-card rounded-lg border border-border">
        <h2 className="text-sm font-semibold mb-2">Configuració Comptable</h2>
        <p className="text-muted-foreground">Paràmetres i configuració del sistema comptable</p>
      </div>
    ),
  },
};

export default function ComptabilitatLayout() {
  const { section } = useParams<{ section: string }>();
  const navigate = useNavigate();

  const config = section && sections[section] ? sections[section] : sections.journals;
  const Component = config.component;
  const Icon = config.icon;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/comptabilitat')}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          title="Tornar a Comptabilitat"
        >
          <ChevronLeft size={20} className="text-muted-foreground" />
        </button>
        <div>
          <div className="flex items-center gap-3">
            <Icon className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-bold text-foreground">{config.title}</h1>
          </div>
          <p className="text-muted-foreground text-sm mt-1">{config.description}</p>
        </div>
      </div>

      <div>
        <Component />
      </div>
    </div>
  );
}
